import type {
  AgentCollectionReadModel,
  AgentReadModel,
  CommunicationReadModel,
  ConsoleSnapshotReadModel,
  ContextCollectionReadModel,
  ContextReadModel,
  IntentReadModel,
  JsonRecord,
  MessageReadModel,
  TaskCollectionReadModel,
  TaskOwnerReadModel,
  TaskReadModel,
  WebSocketTicketReadModel,
} from './models'

export class ConsoleContractError extends Error {
  constructor(readonly path: string, message: string) {
    super(`Invalid Terminal MCP Console response at ${path}: ${message}`)
    this.name = 'ConsoleContractError'
  }
}

function record(value: unknown, path: string): JsonRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new ConsoleContractError(path, 'expected object')
  }
  return value as JsonRecord
}

function array(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw new ConsoleContractError(path, 'expected array')
  return value
}

function string(value: unknown, path: string): string {
  if (typeof value !== 'string') throw new ConsoleContractError(path, 'expected string')
  return value
}

function optionalString(value: unknown, path: string): string | undefined {
  if (value === undefined || value === null) return undefined
  return string(value, path)
}

function integer(value: unknown, path: string): number {
  if (!Number.isInteger(value) || (value as number) < 0) {
    throw new ConsoleContractError(path, 'expected non-negative integer')
  }
  return value as number
}

function optionalInteger(value: unknown, path: string): number | undefined {
  if (value === undefined || value === null) return undefined
  return integer(value, path)
}

function boolean(value: unknown, path: string): boolean {
  if (typeof value !== 'boolean') throw new ConsoleContractError(path, 'expected boolean')
  return value
}

function stringArray(value: unknown, path: string): string[] {
  return array(value, path).map((item, index) => string(item, `${path}[${index}]`))
}

function optionalStringArray(value: unknown, path: string): string[] {
  if (value === undefined || value === null) return []
  return stringArray(value, path)
}

function enumValue<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
): T {
  const parsed = string(value, path)
  if (!allowed.includes(parsed as T)) {
    throw new ConsoleContractError(path, `expected one of ${allowed.join(', ')}`)
  }
  return parsed as T
}

function owner(value: unknown, path: string): TaskOwnerReadModel | undefined {
  if (value === undefined || value === null) return undefined
  const item = record(value, path)
  return {
    agentName: string(item.agent_name, `${path}.agent_name`),
    claimedAt: string(item.claimed_at, `${path}.claimed_at`),
    claimAgeSeconds: integer(item.claim_age_seconds, `${path}.claim_age_seconds`),
    claimIntent: string(item.claim_intent, `${path}.claim_intent`),
    role: enumValue(item.role, ['owner', 'participant'] as const, `${path}.role`),
  }
}

function agent(value: unknown, path: string): AgentReadModel {
  const item = record(value, path)
  return {
    name: string(item.name, `${path}.name`),
    status: string(item.status, `${path}.status`),
    intent: string(item.intent, `${path}.intent`),
    currentStep: integer(item.current_step, `${path}.current_step`),
    lastActivity: string(item.last_activity, `${path}.last_activity`),
    lastActivityAt: string(item.last_activity_at, `${path}.last_activity_at`),
    idleSeconds: optionalInteger(item.idle_seconds, `${path}.idle_seconds`),
    sessionAgeSeconds: optionalInteger(item.session_age_seconds, `${path}.session_age_seconds`),
    taskSummary: optionalString(item.task_summary, `${path}.task_summary`),
    workScope: optionalStringArray(item.work_scope, `${path}.work_scope`),
    messagesAwaitingRead: optionalInteger(
      item.messages_awaiting_read,
      `${path}.messages_awaiting_read`,
    ) ?? 0,
    messagesAwaitingReply: optionalInteger(
      item.messages_awaiting_reply,
      `${path}.messages_awaiting_reply`,
    ) ?? 0,
    alertsPending: optionalInteger(item.alerts_pending, `${path}.alerts_pending`) ?? 0,
  }
}

function task(value: unknown, path: string): TaskReadModel {
  const item = record(value, path)
  const namespace = string(item.namespace, `${path}.namespace`)
  const taskId = string(item.task_id, `${path}.task_id`)
  const known = new Set([
    'namespace', 'task_id', 'title', 'lane', 'priority', 'state', 'operational_status',
    'active', 'archived_at', 'tags', 'next_action', 'candidate_ref', 'owner', 'checkpoint',
    'result',
  ])
  const details: JsonRecord = {}
  for (const [key, raw] of Object.entries(item)) {
    if (!known.has(key)) details[key] = raw
  }

  return {
    key: `${namespace}/${taskId}`,
    namespace,
    taskId,
    title: string(item.title, `${path}.title`),
    lane: enumValue(
      item.lane,
      ['implementation', 'review', 'release', 'integration', 'general'] as const,
      `${path}.lane`,
    ),
    priority: enumValue(item.priority, ['P0', 'P1', 'P2', 'P3'] as const, `${path}.priority`),
    state: enumValue(item.state, ['ready', 'blocked', 'deferred', 'done'] as const, `${path}.state`),
    operationalStatus: enumValue(
      item.operational_status,
      ['ready', 'in_progress', 'blocked', 'deferred', 'done'] as const,
      `${path}.operational_status`,
    ),
    active: boolean(item.active, `${path}.active`),
    archived: item.archived_at !== undefined && item.archived_at !== null,
    tags: stringArray(item.tags ?? [], `${path}.tags`),
    nextAction: string(item.next_action ?? '', `${path}.next_action`),
    candidateRef: optionalString(item.candidate_ref, `${path}.candidate_ref`),
    owner: owner(item.owner, `${path}.owner`),
    checkpoint: item.checkpoint ?? {},
    result: item.result,
    details: Object.keys(details).length > 0 ? details : undefined,
  }
}

function contextEntries(value: unknown, primary: boolean, path: string): ContextReadModel[] {
  if (value === undefined || value === null) return []
  return array(value, path).map((raw, index) => {
    const item = record(raw, `${path}[${index}]`)
    return {
      id: integer(item.id, `${path}[${index}].id`),
      summary: string(item.summary, `${path}[${index}].summary`),
      content: optionalString(item.content, `${path}[${index}].content`),
      primary,
    }
  })
}

function message(value: unknown, path: string): MessageReadModel {
  const item = record(value, path)
  return {
    messageHash: string(item.message_hash, `${path}.message_hash`),
    state: string(item.state, `${path}.state`),
    senderName: string(item.sender_name, `${path}.sender_name`),
    text: string(item.text, `${path}.text`),
    requireReply: typeof item.require_reply === 'boolean' ? item.require_reply : false,
    alert: typeof item.alert === 'boolean' ? item.alert : false,
    createdAt: string(item.created_at, `${path}.created_at`),
  }
}

function intent(value: unknown, path: string): IntentReadModel {
  const item = record(value, path)
  return {
    timestamp: string(item.timestamp, `${path}.timestamp`),
    intent: string(item.intent, `${path}.intent`),
    step: integer(item.step, `${path}.step`),
    workScope: optionalStringArray(item.work_scope, `${path}.work_scope`),
  }
}

function communication(value: unknown, path: string): CommunicationReadModel {
  const item = record(value, path)
  return {
    name: string(item.name, `${path}.name`),
    messagesAwaitingRead: integer(item.messages_awaiting_read ?? 0, `${path}.messages_awaiting_read`),
    messagesAwaitingReply: integer(
      item.messages_awaiting_reply ?? 0,
      `${path}.messages_awaiting_reply`,
    ),
    alertsPending: integer(item.alerts_pending ?? 0, `${path}.alerts_pending`),
    messageJournal: array(item.message_journal ?? [], `${path}.message_journal`).map(
      (raw, index) => message(raw, `${path}.message_journal[${index}]`),
    ),
    intentJournal: array(item.intent_journal ?? [], `${path}.intent_journal`).map(
      (raw, index) => intent(raw, `${path}.intent_journal[${index}]`),
    ),
  }
}

export function decodeAgents(value: unknown, path = '$'): AgentCollectionReadModel {
  const root = record(value, path)
  if (root.ok !== true) throw new ConsoleContractError(`${path}.ok`, 'expected true')
  return {
    agents: array(root.sessions ?? [], `${path}.sessions`).map((raw, index) =>
      agent(raw, `${path}.sessions[${index}]`),
    ),
  }
}

export function decodeTasks(value: unknown, path = '$'): TaskCollectionReadModel {
  const root = record(value, path)
  if (root.ok !== true) throw new ConsoleContractError(`${path}.ok`, 'expected true')
  const tagCountsRaw = record(root.tag_counts ?? {}, `${path}.tag_counts`)
  const tagCounts: Record<string, number> = {}
  for (const [key, raw] of Object.entries(tagCountsRaw)) {
    tagCounts[key] = integer(raw, `${path}.tag_counts.${key}`)
  }
  return {
    tasks: array(root.tasks ?? [], `${path}.tasks`).map((raw, index) =>
      task(raw, `${path}.tasks[${index}]`),
    ),
    summary: record(root.summary ?? {}, `${path}.summary`),
    tagCounts,
  }
}

export function decodeTaskDetail(value: unknown, path = '$'): TaskReadModel {
  const root = record(value, path)
  if (root.ok !== true) throw new ConsoleContractError(`${path}.ok`, 'expected true')
  if (!root.task) throw new ConsoleContractError(`${path}.task`, 'expected task')
  return task(root.task, `${path}.task`)
}

export function decodeContexts(value: unknown, path = '$'): ContextCollectionReadModel {
  const root = record(value, path)
  if (root.ok !== true) throw new ConsoleContractError(`${path}.ok`, 'expected true')
  return {
    contexts: [
      ...contextEntries(root.primary, true, `${path}.primary`),
      ...contextEntries(root.additional, false, `${path}.additional`),
    ],
  }
}

export function decodeSnapshot(value: unknown, path = '$'): ConsoleSnapshotReadModel {
  const root = record(value, path)
  if (root.ok !== true) throw new ConsoleContractError(`${path}.ok`, 'expected true')

  const consistency = record(root.consistency, `${path}.consistency`)
  const mode = string(consistency.mode, `${path}.consistency.mode`)
  if (mode !== 'cursor_first_at_least_once') {
    throw new ConsoleContractError(`${path}.consistency.mode`, 'unsupported consistency mode')
  }
  const highWaterSeq = integer(root.high_water_seq, `${path}.high_water_seq`)
  const replayFromSeq = integer(consistency.replay_from_seq, `${path}.consistency.replay_from_seq`)
  if (integer(consistency.high_water_seq, `${path}.consistency.high_water_seq`) !== highWaterSeq) {
    throw new ConsoleContractError(`${path}.consistency.high_water_seq`, 'must match high_water_seq')
  }

  const instance = record(root.instance, `${path}.instance`)
  const health = record(instance.health, `${path}.instance.health`)
  const agents = decodeAgents({ ok: true, ...record(root.agents, `${path}.agents`) }, `${path}.agents`)
  const tasks = decodeTasks({ ok: true, ...record(root.tasks, `${path}.tasks`) }, `${path}.tasks`)
  const contexts = decodeContexts(
    { ok: true, ...record(root.contexts, `${path}.contexts`) },
    `${path}.contexts`,
  )

  return {
    highWaterSeq,
    replayFromSeq,
    duplicateEventsPossible: boolean(
      consistency.duplicate_events_possible,
      `${path}.consistency.duplicate_events_possible`,
    ),
    instance: {
      application: string(instance.application, `${path}.instance.application`),
      version: string(instance.version, `${path}.instance.version`),
      publicBaseUrl: string(instance.public_base_url, `${path}.instance.public_base_url`),
      healthy: health.ok === true,
      health,
    },
    agents: agents.agents,
    tasks: tasks.tasks,
    contexts: contexts.contexts,
    communications: array(root.communications ?? [], `${path}.communications`).map(
      (raw, index) => communication(raw, `${path}.communications[${index}]`),
    ),
  }
}

export function decodeWebSocketTicket(
  value: unknown,
  path = '$',
): WebSocketTicketReadModel {
  const root = record(value, path)
  return {
    ticket: string(root.ticket, `${path}.ticket`),
    expiresIn: integer(root.expires_in, `${path}.expires_in`),
  }
}
